import {
  Body2,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  makeStyles,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';

import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import SelectItemContent from './SelectItemContent';

import { StatusLabel, ButtonLoading, ControlledTooltip } from '@shared/components';
import { useOneAtATime } from '@shared/hooks';
import { TFolder, TItem, TItemType, TRootItems, TSelectItemDialogState, useFolder } from '@modules/items';
import { TDocument } from '@modules/documents';
import { SEARCHABLE_ITEM_TYPES } from '@modules/search/constants';
import type { TSearchQuery } from '@modules/search/types';
import { TNavigatableFolderType } from '../../../types/dialogs.types';
import { isTFolder } from '../../../types/folders.types';
import SelectItemDialogSearchBox from './SelectItemDialogSearchBox';
import SelectItemSearchContent from './SelectItemSearchContent';

type TSearchOptions = Pick<TSearchQuery, 'itemTypes' | 'blocksCondition' | 'templatesCondition'> & {
  getFolderId?: (data: TFolder | TRootItems) => string | undefined;
  placeholder?: string | ((data?: TFolder | TRootItems) => string);
};

type TProps = {
  initialFolderId?: string;
  rootFolderId?: string;
  initialSelectedItems?: (TDocument | TItem)[];
  state?: TSelectItemDialogState;
  setState: Dispatch<SetStateAction<TSelectItemDialogState | undefined>>;
  title: string;
  actionText: string;
  navigatableFolderTypes: TNavigatableFolderType[];
  selectableTypes: TItemType[];
  isSelectable?: (parent: TFolder | TRootItems, item: TItem) => boolean;
  multiSelect?: boolean;
  checkManageAccess?: boolean;
  getDisabledReason?: (item: TItem) => string | undefined;
  enableSearch?: boolean;
  searchOptions?: TSearchOptions;
};

const useStyles = makeStyles({
  dialogContent: {
    height: '60vh',
    display: 'flex',
    flexDirection: 'column',
  },
  dialogContentBody: {
    flexGrow: 1,
    minHeight: 0,
    overflowY: 'auto',
  },
});

const getSearchableItemTypes = (itemTypes: TItemType[]) =>
  itemTypes.filter((itemType) => SEARCHABLE_ITEM_TYPES.includes(itemType));

const getSearchPlaceholder = (placeholder: TSearchOptions['placeholder'], data?: TFolder | TRootItems) =>
  typeof placeholder === 'function' ? placeholder(data) : placeholder;

const SelectItemDialog = ({
  initialFolderId,
  rootFolderId,
  initialSelectedItems = [],
  state,
  setState,
  title,
  actionText,
  navigatableFolderTypes,
  selectableTypes,
  isSelectable,
  multiSelect = false,
  checkManageAccess = false,
  getDisabledReason,
  enableSearch = false,
  searchOptions,
}: TProps) => {
  const [folderId, setFolderId] = useState<string>(initialFolderId || '');
  const [localSelectedItems, setLocalSelectedItems] = useState<ReadonlyMap<string, TItem>>(new Map());
  const [searchQuery, setSearchQuery] = useState('');

  // When the dialog opens, reset to initial folder and selection.
  useEffect(() => {
    if (state?.isOpen) {
      setFolderId(initialFolderId || '');
      setLocalSelectedItems(initialSelectedItems.reduce((acc, item) => acc.set(item._id, item), new Map()));
      setSearchQuery('');
    }
  }, [state?.isOpen]);

  const { error, data, isLoading: isLoadingFolder } = useFolder(folderId || undefined);

  const [isHandlerLoading, setIsHandlerLoading] = useState(false);
  // Handle the confirm action.
  const handleSelect = useOneAtATime(async (selectedItems: TItem[] = [...localSelectedItems.values()]) => {
    try {
      setIsHandlerLoading(true);
      await state?.onSelect(selectedItems);
    } catch (error) {
      console.error('Error selecting items:', error);
    } finally {
      setIsHandlerLoading(false);
    }
  });

  const searchItemTypes = getSearchableItemTypes(searchOptions?.itemTypes ?? selectableTypes);
  const isSearchActive = enableSearch && searchQuery.trim().length > 0;
  const searchPlaceholder = getSearchPlaceholder(searchOptions?.placeholder, data);

  const continueAction = !isSearchActive && localSelectedItems.size === 0 ? state?.continueAction : undefined;
  const continueDisabledReason = data ? continueAction?.getDisabledReason(data) : undefined;
  const continueFolder = continueAction && data && isTFolder(data) && !continueDisabledReason ? data : undefined;

  const classes = useStyles();

  return (
    <Dialog
      open={state?.isOpen || false}
      onOpenChange={(_e, data) => {
        if (isHandlerLoading) return; // Prevent closing the dialog while loading
        if (state) setState({ ...state, isOpen: data.open });
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} />
              </DialogTrigger>
            }
          >
            {title}
          </DialogTitle>
          <DialogContent className={classes.dialogContent}>
            {enableSearch && (
              <SelectItemDialogSearchBox
                value={searchQuery}
                onChange={setSearchQuery}
                isOpen={Boolean(state?.isOpen)}
                placeholder={searchPlaceholder}
              />
            )}
            <div className={classes.dialogContentBody}>
              <StatusLabel
                data={data}
                loadingMessage="Loading folder..."
                error={error}
                errorMessage="Loading folder failed!"
                render={(data) =>
                  state &&
                  (isSearchActive ? (
                    <SelectItemSearchContent
                      parent={data}
                      query={searchQuery}
                      folderId={searchOptions?.getFolderId?.(data)}
                      itemTypes={searchItemTypes}
                      blocksCondition={searchOptions?.blocksCondition}
                      templatesCondition={searchOptions?.templatesCondition}
                      localSelectedItems={localSelectedItems}
                      setLocalSelectedItems={setLocalSelectedItems}
                      checkManageAccess={checkManageAccess}
                      selectableTypes={selectableTypes}
                      isSelectable={isSelectable}
                      multiSelect={multiSelect}
                      getDisabledReason={getDisabledReason}
                      onPickItem={(item) => handleSelect([item])}
                    />
                  ) : (
                    <SelectItemContent
                      data={data}
                      setFolderId={setFolderId}
                      localSelectedItems={localSelectedItems}
                      setLocalSelectedItems={setLocalSelectedItems}
                      checkManageAccess={checkManageAccess}
                      rootFolderId={rootFolderId}
                      selectableTypes={selectableTypes}
                      isSelectable={isSelectable}
                      navigatableFolderTypes={navigatableFolderTypes}
                      multiSelect={multiSelect}
                      getDisabledReason={getDisabledReason}
                      showNewLink={!state.continueAction}
                    />
                  ))
                }
              />
            </div>
          </DialogContent>
          <DialogActions className="flex items-center">
            {multiSelect && (
              <Body2 className="pr-2 whitespace-nowrap flex-shrink-0">{localSelectedItems.size} selected</Body2>
            )}
            {continueAction ? (
              <ControlledTooltip
                content={continueDisabledReason ?? ''}
                enable={!!continueDisabledReason}
                relationship="inaccessible"
                withArrow
              >
                <Button
                  appearance="primary"
                  className="whitespace-nowrap"
                  disabled={!continueFolder}
                  onClick={() => continueFolder && continueAction.onContinue(continueFolder)}
                >
                  {continueAction.text}
                </Button>
              </ControlledTooltip>
            ) : (
              <ButtonLoading
                appearance="primary"
                disabled={!data || localSelectedItems.size === 0}
                loading={isLoadingFolder || isHandlerLoading}
                onClick={() => handleSelect()}
              >
                {actionText}
              </ButtonLoading>
            )}
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">Cancel</Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default SelectItemDialog;
